import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, Button, Form, Table, Badge, Modal, Alert } from 'react-bootstrap';
import { Clock, MapPin, CheckCircle, XCircle, Eye, Calendar } from 'react-bootstrap-icons';
import PageLayout from '../../layouts/PageLayout';
import { useBranch, useBranchAPI, useFilterParams } from '../../hooks';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';
import { attendanceAPI, employeeAPI, taskAPI } from '../../services';
import GpsCheckInApprovalPanel from './GpsCheckInApprovalPanel';
import { toast } from 'react-toastify';

const ATTENDANCE_VIEWS = ['today', 'week', 'month'];
const ATTENDANCE_STATUSES = ['all', 'ON_TIME', 'LATE', 'ABSENT', 'LEFT_EARLY'];
// #210203 — bộ lọc của bảng "yêu cầu duyệt check-in lỗi GPS". Thông báo gửi quản lý mang link sâu
// `?tab=gps-approvals&gps_status=PENDING&request_id=...` (luật #731599204), nên 3 param này phải
// nằm trong spec — không thì `useFilterParams` coi là param lạ và bỏ đi mất.
const GPS_APPROVAL_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'ALL'];
const isIsoDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v));
const toIsoDate = (d) => {
  // Cắt theo giờ ĐỊA PHƯƠNG. Dùng toISOString() ở đây sẽ lệch 1 ngày với múi giờ VN (UTC+7).
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// #120936 — deep-link màn chấm công. `branch_id` do BranchContext quản.
// `from` là NGÀY MỐC của cửa sổ đang xem; `view` cho biết cửa sổ đó rộng bao nhiêu (ngày/tuần/tháng)
// vì giao diện trang chỉ có 3 chế độ đó, không có ô chọn khoảng tự do.
const ATTENDANCE_FILTER_SPEC = {
  view: { default: 'today', valid: (v) => ATTENDANCE_VIEWS.includes(v) },
  from: { default: () => toIsoDate(new Date()), valid: isIsoDate },
  employee_id: { default: '' },
  status: {
    default: 'all',
    parse: (v) => (v.toLowerCase() === 'all' ? 'all' : v.toUpperCase()),
    valid: (v) => ATTENDANCE_STATUSES.some((s) => s.toLowerCase() === v.toLowerCase()),
  },
  tab: { default: '', valid: (v) => v === '' || v === 'gps-approvals' },
  gps_status: {
    default: 'PENDING',
    parse: (v) => v.toUpperCase(),
    valid: (v) => GPS_APPROVAL_STATUSES.includes(v.toUpperCase()),
  },
  request_id: { default: '' },
};

export default function AttendancePage() {
  const { selectedBranch } = useBranch();
  const { withBranch } = useBranchAPI();

  const [attendances, setAttendances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter states — #120936: nguồn sự thật giờ là URL (deep-link), không còn useState riêng.
  const [urlFilters, setUrlFilters, , { ignored, dismissIgnored }] =
    useFilterParams(ATTENDANCE_FILTER_SPEC);
  const viewMode = urlFilters.view;
  const employeeFilter = urlFilters.employee_id;
  const statusFilter = urlFilters.status;
  const selectedDate = useMemo(() => new Date(`${urlFilters.from}T00:00:00`), [urlFilters.from]);

  const setViewMode = (view) => setUrlFilters({ view });
  const setSelectedDate = (date) => setUrlFilters({ from: toIsoDate(date) });
  const setEmployeeFilter = (employee_id) => setUrlFilters({ employee_id });
  const setStatusFilter = (status) => setUrlFilters({ status });
  const gpsApprovalStatus = urlFilters.gps_status;
  const setGpsApprovalStatus = (gps_status) => setUrlFilters({ gps_status });

  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [shiftTasks, setShiftTasks] = useState([]);
  const [loadingShiftTasks, setLoadingShiftTasks] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    onTime: 0,
    late: 0,
    absent: 0,
    averageHours: 0,
  });

  // Load data
  useEffect(() => {
    loadAttendances();
    loadEmployees();
  }, [selectedBranch?.id, viewMode, selectedDate, employeeFilter, statusFilter]);

  const loadAttendances = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: 1,
        limit: 100,
        branch_id: selectedBranch?.id,
      };

      // Add date filter based on view mode
      if (viewMode === 'today') {
        params.date = toIsoDate(selectedDate);
      } else if (viewMode === 'week') {
        const startOfWeek = new Date(selectedDate);
        startOfWeek.setDate(selectedDate.getDate() - selectedDate.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);

        params.start_date = toIsoDate(startOfWeek);
        params.end_date = toIsoDate(endOfWeek);
      } else if (viewMode === 'month') {
        const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        const endOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);

        params.start_date = toIsoDate(startOfMonth);
        params.end_date = toIsoDate(endOfMonth);
      }

      // Add filters
      if (employeeFilter) {
        params.employee_id = employeeFilter;
      }

      const response = await withBranch(attendanceAPI.getAttendances, params);
      const attendanceData = response.data || response;
      setAttendances(attendanceData);

      // Calculate stats
      calculateStats(attendanceData);
    } catch (err) {
      console.error('Error loading attendances:', err);
      setError('Không thể tải danh sách điểm danh');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await withBranch(employeeAPI.getEmployees, {
        page: 1,
        limit: 100,
        status: 'ACTIVE',
      });
      setEmployees(response.data || response);
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  const calculateStats = (attendanceData) => {
    const total = attendanceData.length;
    const onTime = attendanceData.filter((a) => a.shift?.attendance_status === 'ON_TIME').length;
    const late = attendanceData.filter((a) => a.shift?.attendance_status === 'LATE').length;
    const absent = attendanceData.filter((a) => a.shift?.attendance_status === 'ABSENT').length;

    const totalHours = attendanceData.reduce((sum, a) => sum + (a.actual_hours || 0), 0);
    const averageHours = total > 0 ? (totalHours / total).toFixed(1) : 0;

    setStats({
      total,
      onTime,
      late,
      absent,
      averageHours,
    });
  };

  const handleUpdateAttendance = async (attendanceId, updateData) => {
    try {
      await withBranch(attendanceAPI.updateAttendance, attendanceId, updateData);
      toast.success('Cập nhật điểm danh thành công!');
      setShowEditModal(false);
      loadAttendances();
    } catch (err) {
      console.error('Error updating attendance:', err);
      toast.error('Không thể cập nhật điểm danh');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      PENDING: { variant: 'warning', text: 'Chờ điểm danh' },
      ON_TIME: { variant: 'success', text: 'Đúng giờ' },
      LATE: { variant: 'danger', text: 'Đi trễ' },
      LEFT_EARLY: { variant: 'warning', text: 'Về sớm' },
      ABSENT: { variant: 'secondary', text: 'Vắng mặt' },
      EXCUSED: { variant: 'info', text: 'Có phép' },
    };

    const statusInfo = statusMap[status] || { variant: 'secondary', text: status };
    return <Badge bg={statusInfo.variant}>{statusInfo.text}</Badge>;
  };

  const getCheckInMethodBadge = (method) => {
    const methodMap = {
      IP: { variant: 'primary', text: 'IP' },
      QR_CODE: { variant: 'success', text: 'QR Code' },
      GPS: { variant: 'info', text: 'GPS' },
      MANUAL: { variant: 'warning', text: 'Thủ công' },
    };

    const methodInfo = methodMap[method] || { variant: 'secondary', text: method };
    return <Badge bg={methodInfo.variant}>{methodInfo.text}</Badge>;
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    return new Date(dateTime).toLocaleString('vi-VN');
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('vi-VN');
  };

  const formatHours = (hours) => {
    if (!hours) return 'N/A';
    return `${hours.toFixed(1)}h`;
  };

  const openDetailModal = async (attendance) => {
    setSelectedAttendance(attendance);
    setShiftTasks([]);
    setShowDetailModal(true);
    const shiftId = attendance.shift?.id || attendance.shift_id;
    if (!shiftId) return;
    setLoadingShiftTasks(true);
    try {
      const res = await taskAPI.getShiftTasks({ shift_id: shiftId, limit: 100 });
      setShiftTasks(res?.data || res || []);
    } catch (err) {
      console.error('Error loading shift tasks:', err);
    } finally {
      setLoadingShiftTasks(false);
    }
  };

  const openEditModal = (attendance) => {
    setSelectedAttendance(attendance);
    setShowEditModal(true);
  };


  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header">
          <div className="mc-page-header-title">
            <h1>Điểm danh</h1>
            <p>Theo dõi và quản lý điểm danh của nhân viên</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="mc-card mb-4">
          <Card.Body>
            <InvalidParamsNotice
              ignored={ignored}
              onDismiss={dismissIgnored}
            />
            <Row>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Chế độ xem</Form.Label>
                  <Form.Select
                    value={viewMode}
                    onChange={(e) => setViewMode(e.target.value)}
                  >
                    <option value="today">Hôm nay</option>
                    <option value="week">Tuần này</option>
                    <option value="month">Tháng này</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Ngày</Form.Label>
                  <Form.Control
                    type="date"
                    value={toIsoDate(selectedDate)}
                    onChange={(e) => setSelectedDate(new Date(`${e.target.value}T00:00:00`))}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Nhân viên</Form.Label>
                  <Form.Select
                    value={employeeFilter}
                    onChange={(e) => setEmployeeFilter(e.target.value)}
                  >
                    <option value="">Tất cả</option>
                    {employees.map((emp) => (
                      <option
                        key={emp.id}
                        value={emp.id}
                      >
                        {emp.full_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Trạng thái</Form.Label>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">Tất cả</option>
                    <option value="ON_TIME">Đúng giờ</option>
                    <option value="LATE">Đi trễ</option>
                    <option value="ABSENT">Vắng mặt</option>
                    <option value="LEFT_EARLY">Về sớm</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Stats */}
        <Row className="mb-4">
          <Col md={2}>
            <Card className="mc-card">
              <Card.Body className="text-center">
                <Calendar
                  className="text-primary mb-2"
                  size={32}
                />
                <h4>{stats.total}</h4>
                <p className="text-muted mb-0">Tổng ca</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="mc-card">
              <Card.Body className="text-center">
                <CheckCircle
                  className="text-success mb-2"
                  size={32}
                />
                <h4>{stats.onTime}</h4>
                <p className="text-muted mb-0">Đúng giờ</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="mc-card">
              <Card.Body className="text-center">
                <XCircle
                  className="text-danger mb-2"
                  size={32}
                />
                <h4>{stats.late}</h4>
                <p className="text-muted mb-0">Đi trễ</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="mc-card">
              <Card.Body className="text-center">
                <XCircle
                  className="text-secondary mb-2"
                  size={32}
                />
                <h4>{stats.absent}</h4>
                <p className="text-muted mb-0">Vắng mặt</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="mc-card">
              <Card.Body className="text-center">
                <Clock
                  className="text-info mb-2"
                  size={32}
                />
                <h4>{stats.averageHours}</h4>
                <p className="text-muted mb-0">Giờ TB</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* #210203 — yêu cầu duyệt check-in do lỗi GPS. Đặt TRÊN bảng điểm danh vì đây là việc
            cần xử lý ngay (nhân viên đang đứng ở quán chờ được chấm công), còn bảng dưới là
            dữ liệu để tra cứu. */}
        <GpsCheckInApprovalPanel
          branchId={selectedBranch?.id}
          status={gpsApprovalStatus}
          onStatusChange={setGpsApprovalStatus}
          highlightRequestId={urlFilters.request_id}
        />

        {/* Attendances Table */}
        <Card className="mc-card">
          <Card.Header>
            <h5 className="mb-0">Danh sách điểm danh</h5>
          </Card.Header>
          <Card.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            {loading ? (
              <div className="text-center py-4">
                <div
                  className="spinner-border"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <Table
                responsive
                hover
              >
                <thead>
                  <tr>
                    <th>Nhân viên</th>
                    <th>Ca làm việc</th>
                    <th>Check-in</th>
                    <th>Check-out</th>
                    <th>Giờ làm</th>
                    <th>Trạng thái</th>
                    <th>Phương thức</th>
                    <th>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {attendances.map((attendance) => (
                    <tr key={attendance.id}>
                      <td>
                        <div>
                          <strong>{attendance.employee?.full_name || 'N/A'}</strong>
                          <br />
                          <small className="text-muted">{attendance.employee?.username || 'N/A'}</small>
                        </div>
                      </td>
                      <td>
                        <div>
                          <strong>{formatDateTime(attendance.shift?.start_time)}</strong>
                          <br />
                          <small className="text-muted">đến {formatDateTime(attendance.shift?.end_time)}</small>
                        </div>
                      </td>
                      <td>
                        <div>
                          <Clock className="me-1" />
                          {formatDateTime(attendance.check_in_time)}
                        </div>
                        {attendance.check_in_ip && <small className="text-muted">IP: {attendance.check_in_ip}</small>}
                      </td>
                      <td>
                        <div>
                          <Clock className="me-1" />
                          {formatDateTime(attendance.check_out_time)}
                        </div>
                        {attendance.check_out_ip && <small className="text-muted">IP: {attendance.check_out_ip}</small>}
                      </td>
                      <td>{formatHours(attendance.actual_hours)}</td>
                      <td>{getStatusBadge(attendance.shift?.attendance_status)}</td>
                      <td>
                        {getCheckInMethodBadge(attendance.check_in_method)}
                        {/* #210203: phân biệt rõ với check-in bình thường khi đối chiếu công ca */}
                        {attendance.gps_override_approved && (
                          <div>
                            <Badge bg="warning" text="dark">
                              Duyệt tay do lỗi GPS
                            </Badge>
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => openDetailModal(attendance)}
                          >
                            <Eye />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-warning"
                            onClick={() => openEditModal(attendance)}
                          >
                            Chỉnh sửa
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Detail Modal */}
        <Modal
          show={showDetailModal}
          onHide={() => setShowDetailModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Chi tiết điểm danh</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedAttendance && (
              <div>
                <Row>
                  <Col md={6}>
                    <h6>Thông tin nhân viên</h6>
                    <p>
                      <strong>Tên:</strong> {selectedAttendance.employee?.full_name}
                    </p>
                    <p>
                      <strong>Username:</strong> {selectedAttendance.employee?.username}
                    </p>
                    <p>
                      <strong>Email:</strong> {selectedAttendance.employee?.email}
                    </p>
                  </Col>
                  <Col md={6}>
                    <h6>Thông tin ca làm việc</h6>
                    <p>
                      <strong>Bắt đầu:</strong> {formatDateTime(selectedAttendance.shift?.start_time)}
                    </p>
                    <p>
                      <strong>Kết thúc:</strong> {formatDateTime(selectedAttendance.shift?.end_time)}
                    </p>
                    <p>
                      <strong>Loại ca:</strong> {selectedAttendance.shift?.shift_type}
                    </p>
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col md={6}>
                    <h6>Check-in</h6>
                    <p>
                      <strong>Thời gian:</strong> {formatDateTime(selectedAttendance.check_in_time)}
                    </p>
                    <p>
                      <strong>IP:</strong> {selectedAttendance.check_in_ip || 'N/A'}
                    </p>
                    <p>
                      <strong>Phương thức:</strong> {selectedAttendance.check_in_method}
                    </p>
                    {selectedAttendance.check_in_location && (
                      <p>
                        <strong>Vị trí:</strong> {JSON.stringify(selectedAttendance.check_in_location)}
                      </p>
                    )}
                  </Col>
                  <Col md={6}>
                    <h6>Check-out</h6>
                    <p>
                      <strong>Thời gian:</strong> {formatDateTime(selectedAttendance.check_out_time)}
                    </p>
                    <p>
                      <strong>IP:</strong> {selectedAttendance.check_out_ip || 'N/A'}
                    </p>
                    {selectedAttendance.check_out_location && (
                      <p>
                        <strong>Vị trí:</strong> {JSON.stringify(selectedAttendance.check_out_location)}
                      </p>
                    )}
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col md={12}>
                    <h6>Thông tin khác</h6>
                    <p>
                      <strong>Giờ làm thực tế:</strong> {formatHours(selectedAttendance.actual_hours)}
                    </p>
                    <p>
                      <strong>Trạng thái:</strong> {getStatusBadge(selectedAttendance.shift?.attendance_status)}
                    </p>
                    <p>
                      <strong>Ghi chú:</strong> {selectedAttendance.notes || 'Không có'}
                    </p>
                    <p>
                      <strong>Hợp lệ:</strong> {selectedAttendance.is_valid ? 'Có' : 'Không'}
                    </p>
                  </Col>
                </Row>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowDetailModal(false)}
            >
              Đóng
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Edit Modal */}
        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Chỉnh sửa điểm danh</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedAttendance && (
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Thời gian check-in</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={
                          selectedAttendance.check_in_time
                            ? new Date(selectedAttendance.check_in_time).toISOString().slice(0, 16)
                            : ''
                        }
                        onChange={(e) => {
                          setSelectedAttendance({
                            ...selectedAttendance,
                            check_in_time: e.target.value ? new Date(e.target.value).toISOString() : null,
                          });
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Thời gian check-out</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        value={
                          selectedAttendance.check_out_time
                            ? new Date(selectedAttendance.check_out_time).toISOString().slice(0, 16)
                            : ''
                        }
                        onChange={(e) => {
                          setSelectedAttendance({
                            ...selectedAttendance,
                            check_out_time: e.target.value ? new Date(e.target.value).toISOString() : null,
                          });
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ghi chú</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={selectedAttendance.notes || ''}
                        onChange={(e) => {
                          setSelectedAttendance({
                            ...selectedAttendance,
                            notes: e.target.value,
                          });
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowEditModal(false)}
            >
              Hủy
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                handleUpdateAttendance(selectedAttendance.id, {
                  check_in_time: selectedAttendance.check_in_time,
                  check_out_time: selectedAttendance.check_out_time,
                  notes: selectedAttendance.notes,
                })
              }
            >
              Cập nhật
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </PageLayout>
  );
}

